import { DataType, IndexType } from '@doss/prisma/types'
import _ from 'lodash'
import * as yup from 'yup'
import { DossError } from '../doss-error.js'
import { ActionData, ActionDataArray } from './action-data.js'
import { JsonValue } from './json.js'
import { UserEnteredLogicInput } from './logic.js'
import { isValidArrayInnerType } from './table-column.js'

export type TableCellInput = {
  data: TableCellData | null
  lookupCellIds: string[] | null
  documentIds: string[] | null
  userIds: string[] | null
  arrayOperation?: 'replace' | 'append' | 'remove'
}

/**
 * Represents the type of data stored in a table cell.
 */
export enum DossDataFormat {
  ARRAY = 'ARRAY',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  DATE_TIME = 'DATE_TIME',
  ENUM = 'ENUM',
  FILE = 'FILE',
  FORMULA = 'FORMULA',
  FORMULA_NUMBER = 'FORMULA_NUMBER',
  FORMULA_STRING = 'FORMULA_STRING',
  FORMULA_BOOLEAN = 'FORMULA_BOOLEAN',
  FORMULA_DATE = 'FORMULA_DATE',
  FORMULA_DATE_TIME = 'FORMULA_DATE_TIME',
  ID = 'ID',
  LOOKUP = 'LOOKUP',
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  USER = 'USER',
}

/**
 * For a given {@link DataType}, returns the corresponding {@link DossDataFormat}.
 */
export function dataTypeToDossDataFormat(type: DataType): DossDataFormat {
  switch (type) {
    case DataType.ARRAY:
      return DossDataFormat.ARRAY
    case DataType.BOOLEAN:
      return DossDataFormat.BOOLEAN
    case DataType.DATE:
      return DossDataFormat.DATE
    case DataType.DATE_TIME:
      return DossDataFormat.DATE_TIME
    case DataType.ENUM:
      return DossDataFormat.ENUM
    case DataType.FILE_URL:
      return DossDataFormat.FILE
    case DataType.FORMULA_NUMBER:
      return DossDataFormat.FORMULA_NUMBER
    case DataType.FORMULA_STRING:
      return DossDataFormat.FORMULA_STRING
    case DataType.FORMULA_BOOLEAN:
      return DossDataFormat.FORMULA_BOOLEAN
    case DataType.FORMULA_DATE:
      return DossDataFormat.FORMULA_DATE
    case DataType.FORMULA_DATE_TIME:
      return DossDataFormat.FORMULA_DATE_TIME
    case DataType.ID:
      return DossDataFormat.ID
    case DataType.LOOKUP:
      return DossDataFormat.LOOKUP
    case DataType.NUMBER:
      return DossDataFormat.NUMBER
    case DataType.STRING:
      return DossDataFormat.STRING
    case DataType.USER:
      return DossDataFormat.USER
  }
}

const arrayCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.ARRAY] as const),
    data: yup
      .mixed((value): value is Array<string> | Array<number> | Array<boolean> => {
        return (
          yup.array().of(yup.string().nonNullable().defined()).isValidSync(value) ||
          yup.array().of(yup.number().required()).isValidSync(value) ||
          yup.array().of(yup.boolean().required()).isValidSync(value)
        )
      })
      .required(),
  })
  .strict()
const booleanCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.BOOLEAN] as const),
    data: yup.boolean().required(),
  })
  .strict()
const dateCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.DATE] as const),
    data: yup.string().required(),
  })
  .strict()
const dateTimeCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.DATE_TIME] as const),
    data: yup.string().datetime({ allowOffset: true }).required(),
  })
  .strict()
const enumCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.ENUM] as const),
    data: yup.string().required(),
  })
  .strict()
const fileCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.FILE] as const),
    data: yup.string().optional(),
  })
  .strict()
const formulaCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.FORMULA] as const),
    // Nullable: a not-yet-computed (or computed-to-null) formula cell is a genuine null, not
    // just absent.
    data: yup.number().nullable().optional(),
  })
  .strict()
const formulaNumberCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.FORMULA_NUMBER] as const),
    // Nullable: a not-yet-computed (or computed-to-null) formula cell is a genuine null, not
    // just absent.
    data: yup.number().nullable().optional(),
  })
  .strict()
const formulaStringCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.FORMULA_STRING] as const),
    // Nullable: a not-yet-computed (or computed-to-null) formula cell is a genuine null, not
    // just absent.
    data: yup.string().nullable().optional(),
  })
  .strict()
const formulaDateCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.FORMULA_DATE] as const),
    data: yup.string().nullable().optional(),
  })
  .strict()
const formulaDateTimeCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.FORMULA_DATE_TIME] as const),
    data: yup
      .string()
      .nullable()
      .optional()
      .test('valid-datetime-format', 'Must be a valid ISO 8601 datetime', (value) => {
        if (!value) {
          return true
        }
        return !isNaN(Date.parse(value))
      }),
  })
  .strict()
const idCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.ID] as const),
    data: yup
      .mixed((value): value is string | number => {
        return (
          yup.string().nonNullable().defined().isValidSync(value) ||
          yup.number().required().isValidSync(value)
        )
      })
      .strict()
      .required(),
  })
  .strict()
export const lookupCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.LOOKUP] as const),
    data: yup
      .mixed(
        (
          value,
        ): value is string | number | boolean | Array<string> | Array<number> | Array<boolean> => {
          return (
            yup.string().nonNullable().defined().isValidSync(value) ||
            yup.number().required().isValidSync(value) ||
            yup.boolean().required().isValidSync(value) ||
            yup.array().of(yup.string()).isValidSync(value) ||
            yup.array().of(yup.number()).isValidSync(value) ||
            yup.array().of(yup.boolean()).isValidSync(value)
          )
        },
      )
      .nullable()
      .optional(),
    isOverwritten: yup.boolean().optional(),
    resolvedType: yup.string().oneOf(Object.values(DossDataFormat)).nullable().optional(),
  })
  .strict()
const numberCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.NUMBER] as const),
    data: yup.number().required(),
  })
  .strict()
const stringCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.STRING] as const),
    data: yup.string().nonNullable().defined(),
  })
  .strict()
const userCellDataSchema = yup
  .object({
    type: yup
      .string()
      .required()
      .oneOf([DossDataFormat.USER] as const),
    data: yup.string().optional(),
  })
  .strict()

export type ArrayCellData = yup.InferType<typeof arrayCellDataSchema>
export type BooleanCellData = yup.InferType<typeof booleanCellDataSchema>
export type DateCellData = yup.InferType<typeof dateCellDataSchema>
export type DateTimeCellData = yup.InferType<typeof dateTimeCellDataSchema>
export type EnumCellData = yup.InferType<typeof enumCellDataSchema>
export type FileCellData = yup.InferType<typeof fileCellDataSchema>
export type FormulaCellData = yup.InferType<typeof formulaCellDataSchema>
export type FormulaNumberCellData = yup.InferType<typeof formulaNumberCellDataSchema>
export type FormulaStringCellData = yup.InferType<typeof formulaStringCellDataSchema>
export type FormulaDateCellData = yup.InferType<typeof formulaDateCellDataSchema>
export type FormulaDateTimeCellData = yup.InferType<typeof formulaDateTimeCellDataSchema>
export type IdCellData = yup.InferType<typeof idCellDataSchema>
export type LookupCellData = yup.InferType<typeof lookupCellDataSchema>
export type NumberCellData = yup.InferType<typeof numberCellDataSchema>
export type StringCellData = yup.InferType<typeof stringCellDataSchema>
export type UserCellData = yup.InferType<typeof userCellDataSchema>

export type TableCellData =
  | ArrayCellData
  | BooleanCellData
  | DateCellData
  | DateTimeCellData
  | EnumCellData
  | FileCellData
  | FormulaCellData
  | FormulaNumberCellData
  | FormulaStringCellData
  | FormulaDateCellData
  | FormulaDateTimeCellData
  | IdCellData
  | LookupCellData
  | NumberCellData
  | StringCellData
  | UserCellData

export type FormulaCellDataAny =
  | FormulaCellData
  | FormulaNumberCellData
  | FormulaStringCellData
  | FormulaDateCellData
  | FormulaDateTimeCellData

export type ComputedTableCellData = FormulaCellDataAny | LookupCellData

/** Checks if the data is a valid ArrayCell data */
export function isArrayCellData(data: CellDataInput): data is ArrayCellData {
  return arrayCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid BooleanCell data */
export function isBooleanCellData(data: CellDataInput): data is BooleanCellData {
  return booleanCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid DateCell data */
export function isDateCellData(data: CellDataInput): data is DateCellData {
  return dateCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid DateTimeCell data */
export function isDateTimeCellData(data: CellDataInput): data is DateTimeCellData {
  return dateTimeCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid EnumCell data */
export function isEnumCellData(data: CellDataInput): data is EnumCellData {
  return enumCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid FileCell data */
export function isFileCellData(data: CellDataInput): data is FileCellData {
  return fileCellDataSchema.isValidSync(data)
}

/** Checks if the data is a FormulaCell data */
export function isFormulaCellData(data: CellDataInput): data is FormulaCellData {
  return formulaCellDataSchema.isValidSync(data)
}

/** Checks if the data is a FormulaNumberCell data */
export function isFormulaNumberCellData(data: CellDataInput): data is FormulaNumberCellData {
  return formulaNumberCellDataSchema.isValidSync(data)
}

/** Checks if the data is a FormulaStringCell data */
export function isFormulaStringCellData(data: CellDataInput): data is FormulaStringCellData {
  return formulaStringCellDataSchema.isValidSync(data)
}

/** Checks if the data is a FormulaDateCell data */
export function isFormulaDateCellData(data: CellDataInput): data is FormulaDateCellData {
  return formulaDateCellDataSchema.isValidSync(data)
}

/** Checks if the data is a FormulaDateTimeCell data */
export function isFormulaDateTimeCellData(data: CellDataInput): data is FormulaDateTimeCellData {
  return formulaDateTimeCellDataSchema.isValidSync(data)
}

/** Checks if the data is any formula-typed cell data (FORMULA, FORMULA_NUMBER, FORMULA_STRING, FORMULA_DATE, FORMULA_DATE_TIME) */
export function isFormula(data: CellDataInput): data is FormulaCellDataAny {
  return (
    isFormulaCellData(data) ||
    isFormulaNumberCellData(data) ||
    isFormulaStringCellData(data) ||
    isFormulaDateCellData(data) ||
    isFormulaDateTimeCellData(data)
  )
}

/** Checks if the data is a valid IdCell data */
export function isIdCellData(data: CellDataInput): data is IdCellData {
  return idCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid LookupCell data */
export function isLookupCellData(data: CellDataInput): data is LookupCellData {
  return lookupCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid NumberCell data */
export function isNumberCellData(data: CellDataInput): data is NumberCellData {
  return numberCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid StringCell data */
export function isStringCellData(data: CellDataInput): data is StringCellData {
  return stringCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid UserCell data */
export function isUserCellData(data: CellDataInput): data is UserCellData {
  return userCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid TableCell data */
export function isTableCellData(data: CellDataInput): data is TableCellData {
  if (!data || typeof data !== 'object' || !('type' in data)) {
    return false
  }

  const type = data.type as DossDataFormat
  if (!Object.values(DossDataFormat).includes(type)) {
    return false
  }

  switch (type) {
    case DossDataFormat.ARRAY:
      return isArrayCellData(data)
    case DossDataFormat.BOOLEAN:
      return isBooleanCellData(data)
    case DossDataFormat.DATE:
      return isDateCellData(data)
    case DossDataFormat.DATE_TIME:
      return isDateTimeCellData(data)
    case DossDataFormat.ENUM:
      return isEnumCellData(data)
    case DossDataFormat.FILE:
      return isFileCellData(data)
    case DossDataFormat.FORMULA:
      return isFormulaCellData(data)
    case DossDataFormat.FORMULA_NUMBER:
      return isFormulaNumberCellData(data)
    case DossDataFormat.FORMULA_STRING:
      return isFormulaStringCellData(data)
    case DossDataFormat.FORMULA_DATE:
      return isFormulaDateCellData(data)
    case DossDataFormat.FORMULA_DATE_TIME:
      return isFormulaDateTimeCellData(data)
    case DossDataFormat.FORMULA_BOOLEAN:
      return false
    case DossDataFormat.ID:
      return isIdCellData(data)
    case DossDataFormat.LOOKUP:
      return isLookupCellData(data)
    case DossDataFormat.NUMBER:
      return isNumberCellData(data)
    case DossDataFormat.STRING:
      return isStringCellData(data)
    case DossDataFormat.USER:
      return isUserCellData(data)
  }
}

/**
 * Returns true if the lookup cell data is an overwritten lookup cell.
 */
export function isOverwrittenLookup(data: TableCellData | null): boolean {
  if (!data || !isLookupCellData(data)) {
    return false
  }

  return data.isOverwritten ?? false
}

const resolvedArrayCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.ARRAY] as const),
  /** Only set for array of lookups */
  resolvedType: yup.string().oneOf(Object.values(DossDataFormat)),
  data: yup
    .mixed(
      (value): value is Array<string | null> | Array<number | null> | Array<boolean | null> => {
        return (
          yup.array().of(yup.string().nullable().defined()).isValidSync(value) ||
          yup.array().of(yup.number().nullable().defined()).isValidSync(value) ||
          yup.array().of(yup.boolean().nullable().defined()).isValidSync(value)
        )
      },
    )
    .required(),
})
const resolvedFileCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.FILE] as const),
  /** Stores the name of the file */
  data: yup.string().required(),
})
const resolvedFormulaCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.FORMULA] as const),
  data: yup.number().required(),
})
const resolvedFormulaNumberCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.FORMULA_NUMBER] as const),
  data: yup.number().required(),
})
const resolvedFormulaStringCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.FORMULA_STRING] as const),
  data: yup.string().required(),
})
const resolvedFormulaDateCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.FORMULA_DATE] as const),
  data: yup.string().required(),
})
const resolvedFormulaDateTimeCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.FORMULA_DATE_TIME] as const),
  data: yup.string().datetime({ allowOffset: true }).required(),
})
const resolvedLookupCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.LOOKUP] as const),
  resolvedType: yup.string().required().oneOf(Object.values(DossDataFormat)),
  data: yup
    .mixed(
      (
        value,
      ): value is string | number | boolean | Array<string> | Array<number> | Array<boolean> => {
        return (
          yup.string().nonNullable().defined().isValidSync(value) ||
          yup.number().required().isValidSync(value) ||
          yup.boolean().required().isValidSync(value) ||
          yup.array().of(yup.string()).isValidSync(value) ||
          yup.array().of(yup.number()).isValidSync(value) ||
          yup.array().of(yup.boolean()).isValidSync(value)
        )
      },
    )
    .nullable()
    .defined(),
  isOverwritten: yup.boolean().optional(),
})
const resolvedUserCellDataSchema = yup.object({
  type: yup
    .string()
    .required()
    .oneOf([DossDataFormat.USER] as const),
  /** Stores the name of the user */
  data: yup.string().required(),
})

export type ResolvedArrayCellData = yup.InferType<typeof resolvedArrayCellDataSchema>
export type ResolvedBooleanCellData = BooleanCellData
export type ResolvedDateCellData = DateCellData
export type ResolvedDateTimeCellData = DateTimeCellData
export type ResolvedEnumCellData = EnumCellData
export type ResolvedFileCellData = yup.InferType<typeof resolvedFileCellDataSchema>
export type ResolvedFormulaCellData = yup.InferType<typeof resolvedFormulaCellDataSchema>
export type ResolvedFormulaNumberCellData = yup.InferType<
  typeof resolvedFormulaNumberCellDataSchema
>
export type ResolvedFormulaStringCellData = yup.InferType<
  typeof resolvedFormulaStringCellDataSchema
>
export type ResolvedFormulaDateCellData = yup.InferType<typeof resolvedFormulaDateCellDataSchema>
export type ResolvedFormulaDateTimeCellData = yup.InferType<
  typeof resolvedFormulaDateTimeCellDataSchema
>
export type ResolvedIdCellData = IdCellData
export type ResolvedLookupCellData = yup.InferType<typeof resolvedLookupCellDataSchema>
export type ResolvedNumberCellData = NumberCellData
export type ResolvedStringCellData = StringCellData
export type ResolvedUserCellData = yup.InferType<typeof resolvedUserCellDataSchema>

export type ResolvedTableCellData =
  | ResolvedArrayCellData
  | ResolvedBooleanCellData
  | ResolvedDateCellData
  | ResolvedDateTimeCellData
  | ResolvedEnumCellData
  | ResolvedFileCellData
  | ResolvedFormulaCellData
  | ResolvedFormulaNumberCellData
  | ResolvedFormulaStringCellData
  | ResolvedFormulaDateCellData
  | ResolvedFormulaDateTimeCellData
  | ResolvedIdCellData
  | ResolvedLookupCellData
  | ResolvedNumberCellData
  | ResolvedStringCellData
  | ResolvedUserCellData

/** Checks if the data is a valid ResolvedArrayCell data */
export function isResolvedArrayCellData(data: CellDataInput): data is ResolvedArrayCellData {
  return resolvedArrayCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedBooleanCell data */
export function isResolvedBooleanCellData(data: CellDataInput): data is ResolvedBooleanCellData {
  return booleanCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedDateCell data */
export function isResolvedDateCellData(data: CellDataInput): data is ResolvedDateCellData {
  return dateCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedDateTimeCell data */
export function isResolvedDateTimeCellData(data: CellDataInput): data is ResolvedDateTimeCellData {
  return dateTimeCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedEnumCell data */
export function isResolvedEnumCellData(data: CellDataInput): data is ResolvedEnumCellData {
  return enumCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedFileCell data */
export function isResolvedFileCellData(data: CellDataInput): data is ResolvedFileCellData {
  return resolvedFileCellDataSchema.isValidSync(data)
}

/** Any resolved formula cell, across every formula output subtype (numeric/string/date/datetime). */
export type ResolvedAnyFormulaCellData =
  | ResolvedFormulaCellData
  | ResolvedFormulaNumberCellData
  | ResolvedFormulaStringCellData
  | ResolvedFormulaDateCellData
  | ResolvedFormulaDateTimeCellData

/** Checks if the data is a valid resolved formula cell of any output subtype. */
export function isResolvedFormulaCellData(data: CellDataInput): data is ResolvedAnyFormulaCellData {
  return (
    resolvedFormulaCellDataSchema.isValidSync(data) ||
    resolvedFormulaNumberCellDataSchema.isValidSync(data) ||
    resolvedFormulaStringCellDataSchema.isValidSync(data) ||
    resolvedFormulaDateCellDataSchema.isValidSync(data) ||
    resolvedFormulaDateTimeCellDataSchema.isValidSync(data)
  )
}

/** Checks if the data is a valid ResolvedFormulaStringCell data */
export function isResolvedFormulaStringCellData(
  data: CellDataInput,
): data is ResolvedFormulaStringCellData {
  return resolvedFormulaStringCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedFormulaDateCell data */
export function isResolvedFormulaDateCellData(
  data: CellDataInput,
): data is ResolvedFormulaDateCellData {
  return resolvedFormulaDateCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedFormulaDateTimeCell data */
export function isResolvedFormulaDateTimeCellData(
  data: CellDataInput,
): data is ResolvedFormulaDateTimeCellData {
  return resolvedFormulaDateTimeCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedIdCell data */
export function isResolvedIdCellData(data: CellDataInput): data is ResolvedIdCellData {
  return idCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedLookupCell data */
export function isResolvedLookupCellData(data: CellDataInput): data is ResolvedLookupCellData {
  return resolvedLookupCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedNumberCell data */
export function isResolvedNumberCellData(data: CellDataInput): data is ResolvedNumberCellData {
  return numberCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedStringCell data */
export function isResolvedStringCellData(data: CellDataInput): data is ResolvedStringCellData {
  return stringCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedUserCell data */
export function isResolvedUserCellData(data: CellDataInput): data is ResolvedUserCellData {
  return resolvedUserCellDataSchema.isValidSync(data)
}

/** Checks if the data is a valid ResolvedTableCell data */
export function isResolvedTableCellData(data: CellDataInput): data is ResolvedTableCellData {
  if (data === null || typeof data !== 'object' || !('type' in data)) {
    return false
  }

  const type = data.type as DossDataFormat
  if (!Object.values(DossDataFormat).includes(type)) {
    return false
  }

  switch (type) {
    case DossDataFormat.ARRAY:
      return resolvedArrayCellDataSchema.isValidSync(data)
    case DossDataFormat.BOOLEAN:
      return booleanCellDataSchema.isValidSync(data)
    case DossDataFormat.DATE:
      return dateCellDataSchema.isValidSync(data)
    case DossDataFormat.DATE_TIME:
      return dateTimeCellDataSchema.isValidSync(data)
    case DossDataFormat.ENUM:
      return enumCellDataSchema.isValidSync(data)
    case DossDataFormat.FILE:
      return resolvedFileCellDataSchema.isValidSync(data)
    case DossDataFormat.FORMULA:
      return resolvedFormulaCellDataSchema.isValidSync(data)
    case DossDataFormat.FORMULA_NUMBER:
      return resolvedFormulaNumberCellDataSchema.isValidSync(data)
    case DossDataFormat.FORMULA_STRING:
      return resolvedFormulaStringCellDataSchema.isValidSync(data)
    case DossDataFormat.FORMULA_DATE:
      return resolvedFormulaDateCellDataSchema.isValidSync(data)
    case DossDataFormat.FORMULA_DATE_TIME:
      return resolvedFormulaDateTimeCellDataSchema.isValidSync(data)
    // Boolean-output formulas are out of scope for the non-numeric-formulas epic; no formula can
    // ever produce this cell data today.
    case DossDataFormat.FORMULA_BOOLEAN:
      return false
    case DossDataFormat.ID:
      return idCellDataSchema.isValidSync(data)
    case DossDataFormat.LOOKUP:
      return resolvedLookupCellDataSchema.isValidSync(data)
    case DossDataFormat.NUMBER:
      return numberCellDataSchema.isValidSync(data)
    case DossDataFormat.STRING:
      return stringCellDataSchema.isValidSync(data)
    case DossDataFormat.USER:
      return resolvedUserCellDataSchema.isValidSync(data)
  }
}

/**
 * Returns the numeric value if it is a valid numeric index value, i.e. it is a non-negative integer
 * without any leading zeros. This function can be used when the input cell value could be a string
 * but the corresponding index value is expected to be a number.
 */
export function getNumberIndexValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return value
  } else if (typeof value === 'string') {
    return getNumberIndexValue(Number(value))
  }
  return
}

/**
 * Strips the prefix and suffix from the given index value and returns it.
 */
export function stripIndexAffixes(index: string, prefix: string, suffix: string): string {
  if (prefix.length > 0 && index.startsWith(prefix)) {
    index = index.slice(prefix.length)
  }
  if (suffix.length > 0 && index.endsWith(suffix)) {
    index = index.slice(0, -1 * suffix.length)
  }
  return index
}

/**
 * Returns the user-visible value for an index cell. This function handles nullish input values.
 */
export function getUserVisibleIndexValue(
  indexValue: string | number | null | undefined,
  prefix: string,
  suffix: string,
): string {
  if (_.isNil(indexValue)) {
    return ''
  }
  return [prefix, indexValue, suffix].join('')
}

type CellDataInput = Record<string, unknown> | JsonValue

// Empty ID cell data would be invalid because any cell data would be a valid index value, therefore
// we return null.
export function getEmptyIdCell(): null {
  return null
}

// Gets the initial cell data for a string cell
export function getEmptyStringCell(): StringCellData {
  return {
    type: DossDataFormat.STRING,
    data: '',
  }
}

// Gets the initial cell data for a number cell
export function getEmptyNumberCell(): NumberCellData {
  return {
    type: DossDataFormat.NUMBER,
    data: 0,
  }
}

// An empty file cell would be invalid because the data comes from cell.documents. Therefore, we
// return just the type as the empty file cell.
export function getEmptyFileCell(): FileCellData {
  return { type: DossDataFormat.FILE }
}

// An empty user cell would be invalid because the data comes from cell.users. Therefore, we
// return just the type as the empty user cell.
export function getEmptyUserCell(): UserCellData {
  return { type: DossDataFormat.USER }
}

// Gets the initial cell data for a boolean cell
export function getEmptyBooleanCell(): BooleanCellData {
  return {
    type: DossDataFormat.BOOLEAN,
    data: false,
  }
}

// Gets the initial cell data for a date cell
export function getEmptyDateCell(): DateCellData {
  return {
    type: DossDataFormat.DATE,
    data: '',
  }
}

// Gets the initial cell data for a date time cell
export function getEmptyDateTimeCell(): DateTimeCellData {
  return {
    type: DossDataFormat.DATE_TIME,
    data: '',
  }
}

// Gets the initial cell data for an enum cell
export function getEmptyEnumCell(): EnumCellData {
  return {
    type: DossDataFormat.ENUM,
    data: '',
  }
}

// Gets the initial cell data for an array cell
export function getEmptyArrayCell(): ArrayCellData {
  return {
    type: DossDataFormat.ARRAY,
    data: [],
  }
}

export function getEmptyFormulaCell(): FormulaCellData {
  return { type: DossDataFormat.FORMULA }
}

export function getEmptyFormulaNumberCell(): FormulaNumberCellData {
  return { type: DossDataFormat.FORMULA_NUMBER }
}

export function getEmptyFormulaStringCell(): FormulaStringCellData {
  return { type: DossDataFormat.FORMULA_STRING }
}

export function getEmptyFormulaDateCell(): FormulaDateCellData {
  return { type: DossDataFormat.FORMULA_DATE }
}

export function getEmptyFormulaDateTimeCell(): FormulaDateTimeCellData {
  return { type: DossDataFormat.FORMULA_DATE_TIME }
}

/** Get primitive value for a table cell, or null if there is no value or if it is computed */
export function getTableCellPrimitiveValue(cellData: TableCellData): ActionData | null {
  switch (cellData.type) {
    case DossDataFormat.ID:
    case DossDataFormat.STRING:
    case DossDataFormat.NUMBER:
    case DossDataFormat.ENUM:
    case DossDataFormat.DATE:
    case DossDataFormat.DATE_TIME:
    case DossDataFormat.BOOLEAN:
      return cellData.data
    case DossDataFormat.ARRAY:
      // Filter nulls from array (can occur in lookup arrays when referenced row has null value)
      return Array.isArray(cellData.data)
        ? (cellData.data.filter(
            (v): v is string | number | boolean => v !== null,
          ) as ActionDataArray)
        : cellData.data
    case DossDataFormat.FORMULA:
    case DossDataFormat.FORMULA_NUMBER:
    case DossDataFormat.FORMULA_STRING:
    case DossDataFormat.FORMULA_DATE:
    case DossDataFormat.FORMULA_DATE_TIME:
      return cellData.data ?? null
    case DossDataFormat.LOOKUP:
    case DossDataFormat.FILE:
    case DossDataFormat.USER:
      return cellData?.data ?? null
  }
}

// Gets the initial cell data for a lookup cell
export function getEmptyLookupCell(): LookupCellData {
  return { type: DossDataFormat.LOOKUP }
}

// Gets the initial cell data for a cell based on the column type
export function getEmptyCellData(column: {
  type: DataType
  innerType?: DataType | null
}): TableCellData | ArrayCellData | null {
  switch (column.type) {
    case DataType.STRING:
      return getEmptyStringCell()
    case DataType.NUMBER:
      return getEmptyNumberCell()
    case DataType.BOOLEAN:
      return getEmptyBooleanCell()
    case DataType.DATE:
      return getEmptyDateCell()
    case DataType.DATE_TIME:
      return getEmptyDateTimeCell()
    case DataType.ENUM:
      return getEmptyEnumCell()
    case DataType.FILE_URL:
      return getEmptyFileCell()
    case DataType.USER:
      return getEmptyUserCell()
    case DataType.ID:
      return getEmptyIdCell()
    case DataType.LOOKUP:
      return getEmptyLookupCell()
    case DataType.ARRAY:
      return getEmptyArrayCell()
    case DataType.FORMULA_NUMBER:
      return getEmptyFormulaNumberCell()
    case DataType.FORMULA_STRING:
      return getEmptyFormulaStringCell()
    case DataType.FORMULA_DATE:
      return getEmptyFormulaDateCell()
    case DataType.FORMULA_DATE_TIME:
      return getEmptyFormulaDateTimeCell()
    // Boolean-output formulas are out of scope for the non-numeric-formulas epic; no formula can
    // ever produce this cell data today.
    case DataType.FORMULA_BOOLEAN:
      return null
  }
}

/**
 * Given a default value and the column type it should match, returns a Cell data object for it
 */
export function convertDefaultValueToCellData<T extends { id: string }>(
  defaultValue: unknown,
  type: DataType,
  innerType: DataType | null,
  indexType: IndexType,
): UserEnteredLogicInput {
  if (_.isNil(defaultValue)) {
    let data: TableCellData | null = null
    if (type === DataType.LOOKUP) {
      data = getEmptyLookupCell()
    } else if (type === DataType.ARRAY) {
      data = getEmptyArrayCell()
    } else if (type === DataType.FORMULA_NUMBER) {
      data = getEmptyFormulaNumberCell()
    } else if (type === DataType.FORMULA_STRING) {
      data = getEmptyFormulaStringCell()
    } else if (type === DataType.FORMULA_DATE) {
      data = getEmptyFormulaDateCell()
    } else if (type === DataType.FORMULA_DATE_TIME) {
      data = getEmptyFormulaDateTimeCell()
    } else if (type === DataType.FILE_URL) {
      data = getEmptyFileCell()
    } else if (type === DataType.USER) {
      data = getEmptyUserCell()
    } else if (type === DataType.BOOLEAN) {
      data = getEmptyBooleanCell()
    }
    return { data, lookupCellIds: null, documentIds: null, userIds: null }
  }

  let data: TableCellData | null = null
  let lookupCellIds: string[] | null = null
  let documentIds: string[] | null = null
  let userIds: string[] | null = null
  switch (type) {
    case DataType.ARRAY: {
      if (!Array.isArray(defaultValue)) {
        throw new DossError('Default value must be an array for array type', [])
      }

      if (!isValidArrayInnerType(innerType)) {
        throw new DossError('Invalid inner type for array column: %s', [innerType])
      }

      switch (innerType) {
        case DataType.LOOKUP: {
          const isTableCellArray =
            Array.isArray(defaultValue) &&
            defaultValue.every((cell) => cell !== null && typeof cell === 'object' && 'id' in cell)
          if (isTableCellArray) {
            lookupCellIds = defaultValue.map((cell) => (cell as T).id)
          }
          data = { type: DossDataFormat.ARRAY, data: [] }
          break
        }
        case DataType.FILE_URL: {
          documentIds = _.compact(defaultValue.map((id) => id?.toString()))
          data = { type: DossDataFormat.ARRAY, data: [] }
          break
        }
        case DataType.USER: {
          // Accept both `{id: string}` objects (legacy default-value shape) and plain
          // string IDs (the shape returned by the z-table read path for USER[]).
          userIds = _.compact(
            defaultValue.map((cell) =>
              typeof cell === 'string'
                ? cell
                : typeof cell === 'object' && cell !== null && 'id' in cell
                  ? (cell as T).id
                  : null,
            ),
          )
          data = { type: DossDataFormat.ARRAY, data: [] }
          break
        }
        case DataType.ENUM: {
          data = { type: DossDataFormat.ARRAY, data: defaultValue as string[] }
          break
        }
      }
      break
    }
    case DataType.FORMULA_NUMBER: {
      data = {
        type: DossDataFormat.FORMULA_NUMBER,
        data: defaultValue as number,
      }
      break
    }
    case DataType.FORMULA_STRING: {
      data = { type: DossDataFormat.FORMULA_STRING, data: defaultValue as string }
      break
    }
    case DataType.FORMULA_DATE: {
      data = { type: DossDataFormat.FORMULA_DATE, data: defaultValue as string }
      break
    }
    case DataType.FORMULA_DATE_TIME: {
      data = { type: DossDataFormat.FORMULA_DATE_TIME, data: defaultValue as string }
      break
    }
    // Boolean-output formulas are out of scope for the non-numeric-formulas epic; no formula can
    // ever produce this cell data today.
    case DataType.FORMULA_BOOLEAN:
      break
    case DataType.ID: {
      let idValue: string | number
      switch (indexType) {
        case IndexType.NONE:
          throw new DossError('Cannot create an ID cell with no index type', [])
        case IndexType.NUMBER: {
          const isValidNumber =
            typeof defaultValue === 'number' && Number.isInteger(defaultValue) && defaultValue >= 0
          const isValidString = typeof defaultValue === 'string' && defaultValue.length > 0
          if (isValidNumber) {
            idValue = defaultValue
          } else if (isValidString) {
            idValue = parseInt(defaultValue)
          } else {
            throw new DossError('Invalid default value for ID column: %s', [String(defaultValue)])
          }
          break
        }
        case IndexType.STRING: {
          if (typeof defaultValue !== 'string') {
            throw new DossError('Invalid default value for ID column: %s', [String(defaultValue)])
          }
          idValue = defaultValue.toString().trim()
          break
        }
      }
      data = { type: DossDataFormat.ID, data: idValue }
      break
    }
    case DataType.STRING: {
      if (typeof defaultValue !== 'string') {
        throw new DossError('Invalid default value for string column: %s', [String(defaultValue)])
      }
      data = {
        type: DossDataFormat.STRING,
        data: defaultValue.toString().trim(),
      }
      break
    }
    case DataType.NUMBER: {
      let numberValue: number
      if (typeof defaultValue === 'number') {
        numberValue = defaultValue
      } else if (typeof defaultValue === 'string') {
        numberValue = parseFloat(defaultValue)
      } else {
        throw new DossError('Invalid default value for number column: %s', [String(defaultValue)])
      }
      data = { type: DossDataFormat.NUMBER, data: numberValue }
      break
    }
    case DataType.FILE_URL:
      if (typeof defaultValue !== 'string' && !Array.isArray(defaultValue)) {
        throw new DossError('Invalid default value for file column: %s', [String(defaultValue)])
      }
      data = { type: DossDataFormat.FILE }
      if (typeof defaultValue === 'string' && defaultValue !== '') {
        documentIds = [defaultValue]
      } else if (Array.isArray(defaultValue)) {
        documentIds = defaultValue.map((id) => id.toString())
      }
      break
    case DataType.USER: {
      // Cannot set default value for user column.
      data = { type: DossDataFormat.USER }
      const isTableCell =
        typeof defaultValue === 'object' && !_.isNil(defaultValue) && 'id' in defaultValue
      if (isTableCell) {
        if (typeof defaultValue.id === 'string') {
          userIds = [defaultValue.id]
        }
      } else if (typeof defaultValue === 'string' && defaultValue !== '') {
        userIds = [defaultValue]
      } else if (Array.isArray(defaultValue) && defaultValue.length === 1) {
        const userId = defaultValue[0].id
        userIds = [userId]
      }
      break
    }
    case DataType.BOOLEAN:
      data = { type: DossDataFormat.BOOLEAN, data: Boolean(defaultValue) }
      break
    case DataType.DATE:
      if (typeof defaultValue !== 'string') {
        throw new DossError('Invalid default value for date column: %s', [String(defaultValue)])
      }
      data = {
        type: DossDataFormat.DATE,
        data: defaultValue.toString(),
      }
      break
    case DataType.DATE_TIME:
      if (typeof defaultValue !== 'string') {
        throw new DossError('Invalid default value for date time column: %s', [
          String(defaultValue),
        ])
      }
      data = {
        type: DossDataFormat.DATE_TIME,
        data: defaultValue.toString(),
      }
      break
    case DataType.ENUM:
      if (typeof defaultValue !== 'string' && !Array.isArray(defaultValue)) {
        throw new DossError('Invalid default value for enum column: %s', [String(defaultValue)])
      }
      data = { type: DossDataFormat.ENUM, data: defaultValue.toString() }
      break
    case DataType.LOOKUP: {
      // Check if user is overriding the lookup
      if (typeof defaultValue === 'object' && isLookupCellData(defaultValue)) {
        data = defaultValue
        lookupCellIds = []
      } else {
        // Cannot set default value for lookup column. Instead set the lookup column ID
        data = { type: DossDataFormat.LOOKUP }
        // Check if default value is an array of cells or strings
        if (Array.isArray(defaultValue)) {
          if (defaultValue.every(checkIsTableCell)) {
            lookupCellIds = defaultValue.map((value) => value.id)
          } else if (defaultValue.every((value) => typeof value === 'string')) {
            lookupCellIds = defaultValue
          }
        }
        const isTableCell = checkIsTableCell(defaultValue)
        if (isTableCell) {
          if (typeof defaultValue.id === 'string') {
            lookupCellIds = [defaultValue.id]
          }
        }
      }

      break
    }
  }
  return { data, lookupCellIds, documentIds, userIds }
}

/**
 * Checks if the value passed in is a table cell
 */
function checkIsTableCell(value: unknown): value is { id: string } {
  return typeof value === 'object' && !_.isNil(value) && 'id' in value
}
